// AppFormModal.jsx
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Table,
} from "antd";
import { useEffect, useRef } from "react";

const { Title } = Typography;
const { TextArea } = Input;

import {
  formatSubmitValue,
  parseDefaultValue,
} from "../../../utils/helpers/formFieldUtils";

const AppFormModal = ({
  title,
  open,
  onClose,
  onValuesChange,
  fields = [],
  onSubmit,
  form,
  mode = "add",
  persona,
  extraSections = [],
}) => {
  const isViewMode = mode === "view";

  // watch all form values for conditional rendering
  const values = Form.useWatch([], form);

  // store initial values at modal open
  const initialValuesRef = useRef({});

  // Populate form when opened and capture frozen values
  useEffect(() => {
    if (open) {
      const initialValues = {};
      fields.forEach((f) => {
        initialValues[f.name] = parseDefaultValue(f, f.defaultValue);
      });

      form.setFieldsValue(initialValues);
      initialValuesRef.current = initialValues;
    } else {
      form.resetFields();
      initialValuesRef.current = {};
    }
  }, [open, form]);

  const handleFinish = (values) => {
    const formattedValues = {};
    fields.forEach((f) => {
      formattedValues[f.name] = formatSubmitValue(f, values[f.name]);
    });
    onSubmit?.(formattedValues, form);
  };

  const handleClose = () => {
    form.resetFields();
    initialValuesRef.current = {};
    onClose();
  };

  const renderField = (field) => {
    if (field.type === "empty" || field.type === "spacer") return null;

    if (field.type === "title" || field.type === "section") {
      return (
        <>
          {field.divider && <Divider />}
          <Title style={{ color: "#008AD1" }} level={field.level || 5}>
            {field.label}
          </Title>
        </>
      );
    }

    const commonProps = {
      disabled: isViewMode || field?.readOnly || field?.disabled,
      readOnly: isViewMode || field?.readOnly,
      placeholder: "Please enter",
    };

    switch (field.type) {
      case "date":
        return (
          <DatePicker
            format="YYYY-MM-DD"
            size="large"
            picker="date"
            disabledDate={field.disabledDate}
            {...commonProps}
          />
        );

      case "select": {
        let optionsToRender = field.options || [];

        // If field.freezeOptionsIn includes this mode, lock options snapshot
        if (field.freezeOptionsIn?.includes(mode)) {
          optionsToRender = field.frozenOptions || [...optionsToRender];
        }

        // Reference value for allowedOptionsMap
        const referenceVal =
          initialValuesRef.current?.[field.name] ??
          form.getFieldValue(field.name);

        // ✅ Only apply allowedOptionsMap if we have a reference value
        if (
          field.allowedOptionsMap &&
          field.freezeOptionsIn?.includes(mode) &&
          referenceVal
        ) {
          const allowedKeys = field.allowedOptionsMap[referenceVal] || [];
          optionsToRender = optionsToRender.map((opt) => ({
            ...opt,
            disabled: !allowedKeys.includes(opt.key) || !!opt.disabled,
          }));
        }

        // Combine with custom runtime disabler
        const finalOptions = optionsToRender.map((v) => {
          const runtimeDisabled = field.optionDisabler
            ? field.optionDisabler(v, values, mode, persona)
            : false;
          return {
            value: v.key,
            label: v.label,
            disabled: !!v.disabled || runtimeDisabled,
          };
        });

        return (
          <Select
            size="large"
            showSearch
            allowClear
            placeholder="Please select"
            options={finalOptions}
            {...commonProps}
          />
        );
      }

      case "textarea":
        return (
          <TextArea autoSize={{ minRows: 3, maxRows: 5 }} {...commonProps} />
        );

      default:
        return <Input size="large" type={field.type} {...commonProps} />;
    }
  };

  const renderExtraSection = (section, idx) => (
    <div key={`extra-${idx}`} style={{ marginTop: 24 }}>
      <Divider />
      <Title level={5} style={{ marginBottom: 12 }}>
        {section.title}
      </Title>

      {section.type === "table" && (
        <Table
          rowKey={section.rowKey || "id"}
          dataSource={section.data || []}
          columns={section.columns || []}
          pagination={false}
          size="small"
          scroll={{ y: 240 }}
        />
      )}

      {section.type === "custom" && section.render?.()}
    </div>
  );

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={handleClose}
      open={open}
      width={600}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={onValuesChange}
        onFinish={handleFinish}
      >
        <Row gutter={[16, 0]}>
          {fields
            .filter((field) => {
              if (field.hide === true) return false;
              if (field.showForPersona) {
                return field.showForPersona.includes(persona);
              }
              if (field.hideForPersona) {
                return !field.hideForPersona.includes(persona);
              }
              if (field.showInModes) {
                return field.showInModes.includes(mode);
              }
              return true;
            })
            .map((field) => {
              if (field.showWhen && !field.showWhen(values, mode, persona)) {
                return null;
              }
              return (
                <Col
                  key={field.name}
                  {...(field.colspan
                    ? { span: field.colspan }
                    : { xs: 24, sm: 12 })}
                >
                  {field.type === "empty" ? (
                    <div style={{ height: field.height || "1rem" }} />
                  ) : field.type === "title" || field.type === "section" ? (
                    renderField(field)
                  ) : (
                    <Form.Item
                      label={field.label}
                      name={field.name}
                      rules={
                        field.dynamicRules
                          ? field.dynamicRules(values, mode, persona)
                          : field.rules?.length
                          ? field.rules
                          : field.required
                          ? [
                              {
                                required: true,
                                message:
                                  field.errorMessage ||
                                  "This field is required",
                              },
                            ]
                          : []
                      }
                    >
                      {renderField(field)}
                    </Form.Item>
                  )}
                </Col>
              );
            })}
        </Row>

        {/* Extra sections */}
        {extraSections
          .filter((section) => !section.showIn || section.showIn.includes(mode))
          .map(renderExtraSection)}

        <Form.Item style={{ marginTop: 16 }}>
          <Space className="w-100 justify-content-end">
            <Button htmlType="button" onClick={handleClose}>
              {mode === "view" ? "Close" : "Cancel"}
            </Button>
            {mode !== "view" && (
              <Button
                className="btn btn-primary"
                type="primary"
                htmlType="submit"
              >
                {mode === "edit" ? "Update" : "Submit"}
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AppFormModal;
